import { MessageMedium } from 'constants/messageMediums';
import { SavedRow, UpsertSavedRowVariables } from 'modules/dragAndDropEditor/types';
import { match, P } from 'ts-pattern';
import {
  CREATE_OR_EDIT_SAVED_ROW_FORM_FIELD_NAMES,
  CreateOrEditSavedRowFormValues,
} from './CreateOrEditSavedRowFormProvider';

export const parseSavedRowFormValue = (values: CreateOrEditSavedRowFormValues) => {
  const { rowName, category } = values;
  const categoryOption = category ?? ({} as CreateOrEditSavedRowFormValues['category']);
  const categoryId = categoryOption?.value;
  const categoryName = categoryOption?.label;

  return (
    match(categoryOption)
      /**
       * Creating a new category (i.e. only `name` is available)
       * NOTE: it is possible that the category `id` and `name` are the same for existing categories
       * (e.g. if a customer name a category the same as the id), but unlikely.
       */
      .with({ label: categoryId }, () => ({ rowName, categoryName, categoryId: undefined }))
      /** Selecting an existing category (i.e `id` + `name` are available) */
      .with({ label: P.when(Boolean), value: P.when(Boolean) }, () => ({
        rowName,
        categoryId: Number(categoryId),
        categoryName: undefined,
      }))
      /** no category selection (i.e no `id` or `name`) */
      .otherwise(() => ({ rowName, categoryName: undefined, categoryId: undefined }))
  );
};

/**
 * Generates payload for creating a saved row
 */
export const generateCreateSavedRowPayload = (
  values: CreateOrEditSavedRowFormValues,
  additionalData: {
    messageMedium: MessageMedium;
    html: string;
    beeMetadata: string;
  },
): UpsertSavedRowVariables => {
  const userManagedValues = parseSavedRowFormValue(values);
  const { messageMedium, html, beeMetadata } = additionalData;

  return { ...userManagedValues, messageMedium, html, beeMetadata };
};

/**
 * Generates payload for updating a saved row
 */
export const generateUpdateSavedRowPayload = (
  values: CreateOrEditSavedRowFormValues,
  savedRow: SavedRow,
) => {
  const rowId = savedRow?.id;
  const userManagedValues = parseSavedRowFormValue(values);

  return { ...userManagedValues, rowId };
};

export const getSavedRowFormInitialValues = (
  savedRow?: SavedRow,
): CreateOrEditSavedRowFormValues => {
  if (savedRow) {
    return {
      [CREATE_OR_EDIT_SAVED_ROW_FORM_FIELD_NAMES.ROW_NAME]: savedRow?.name ?? '',
      // if category was not set before (aka null), set it to null
      [CREATE_OR_EDIT_SAVED_ROW_FORM_FIELD_NAMES.CATEGORY]: savedRow?.category
        ? {
            label: savedRow?.category?.name ?? '',
            value: savedRow?.category?.id?.toString() ?? '',
          }
        : null,
    };
  } else {
    return {
      [CREATE_OR_EDIT_SAVED_ROW_FORM_FIELD_NAMES.ROW_NAME]: '',
      [CREATE_OR_EDIT_SAVED_ROW_FORM_FIELD_NAMES.CATEGORY]: null,
    };
  }
};
